import { tWithKeyAttr as t } from "../../i18nHelper";
import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import chatStyles from "./chat.module.css";
import {
  chatNoteTypes,
  formatDateTime,
  mobileWidth,
  noDataLabel,
  TYPING_CODE,
} from "../../utils/constants";
import useMediaQuery from "../../hooks/useMediaQuery";
import { useAppSelector } from "../../services/hooks";
import { userSelector } from "../../services/selectors/user.selectors";
import defaultAvatar from "../../images/default_avatar.png";
import useAutosizeTextArea from "../../hooks/useAutosizeTextarea";
import { getDate } from "../../utils/utils";
import ChatSideBar from "../chatSidebar/chatSideBar";
import useTypingIndicator from "../../services/hooks/useTypingIndicator";
import {
  callEndedSelector,
  chatConnectionSelector,
} from "../../services/selectors/chat.selector";
import { IChatMessage } from "../../types/types";
import MessageTest from "../message/messageTest";
import { SocketContext } from "../../SocketContext";
import CallEndedNotificationPopup from "../modals/callEndedNotification/callEndedNotificationPopup";
import Modal from "../modals/modal/Modal";
import ChatNote from "../chatNote/chatNote";
import { useActions } from "../../services/hooks/useActions";
import { IConversation } from "../../services/types";
import Tooltip from "../tooltip/tooltip";
import { useUpdateChatAppointmentMutation } from "../../services/api/user.api";
import { TUpdateChatAppointmentEntry } from "../../services/types/user.types";
import i18n from "../../i18n";

const Chat = ({
  openMenu,
  activeUser,
  setActiveUser,
}: {
  openMenu: boolean;
  activeUser: undefined | IConversation;
  setActiveUser: Dispatch<SetStateAction<undefined | IConversation>>;
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  const mobile = useMediaQuery(mobileWidth);
  const therapist = useAppSelector(userSelector);
  const connection = useAppSelector(chatConnectionSelector);
  const isConnected = connection?.state === "Connected";
  const callEnded = useAppSelector(callEndedSelector);

  const {
    endCall,
    updateAppointmentStarted,
    setUnReadMessages,
    setUnReadMessagesForUser,
  } = useActions();

  const { callUser, startCall, leaveCall, stream, acknowledgeRemoteEnd } =
    useContext(SocketContext);

  const [showTooltip, setShowTooltip] = useState(false);

  // endCall if user disconnected
  useEffect(
    () => {
      if (startCall && !activeUser?.online) {
        leaveCall();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeUser?.online]
  );
  const [value, setValue] = useState("");
  const [openSideBar, setOpenSideBar] = useState(true);
  const [showCharLimitError, setShowCharLimitError] = useState(false);

  useAutosizeTextArea(textAreaRef.current, value);

  const { isTyping, startTyping } = useTypingIndicator(4000);
  const [updateChatAppointment] = useUpdateChatAppointmentMutation();

  const handleSendMessage = useCallback(
    (inputValue: string, type: string) => {
      if (!isConnected || !inputValue) return;
      const message = {
        type,
        senderId: Number(therapist.id),
        content: inputValue,
        receiverId: activeUser?.userId,
        appointmentId: activeUser?.appointment?.id,
      };
      // update startTime and date for appointments missing these parameters
      if (activeUser?.appointment && !activeUser.hasStarted) {
        const body: TUpdateChatAppointmentEntry[] = [
          {
            op: "replace",
            path: "/StartTime",
            value: formatDateTime(new Date()).split("T")[1].replace("Z", ""),
          },
          {
            op: "replace",
            path: "/AppointmentDate",
            value: formatDateTime(new Date()),
          },
        ];
        updateChatAppointment({
          body,
          appointmentId: activeUser.appointment.id,
        });
        updateAppointmentStarted(activeUser);
      }
      if (activeUser) {
        connection?.invoke("SendMessage", message);
      }
    },
    [
      isConnected,
      connection,
      activeUser,
      therapist.id,
      updateAppointmentStarted,
      updateChatAppointment,
    ]
  );

  useEffect(() => {
    const listener = (event: KeyboardEvent) => {
      const activeElement = document.activeElement;
      const isTextareaFocused = activeElement?.tagName === "TEXTAREA";

      if (!isTextareaFocused) return; // Don't send if focus is not on textarea

      if (event.code === "Enter" && event.ctrlKey) {
        event.preventDefault();
        setValue((prev) => `${prev}\n`); // Add a new line to the text
      } else if (
        (event.code === "Enter" || event.code === "NumpadEnter") &&
        !event.shiftKey
      ) {
        event.preventDefault();
        const trimmed = value.trim();
        if (!trimmed) return;
        handleSendMessage(trimmed, "Message");
        setValue("");
      }
    };

    document.addEventListener("keydown", listener);
    return () => {
      document.removeEventListener("keydown", listener);
    };
  }, [handleSendMessage, value]);

  useEffect(() => {
    if (isConnected) {
      handleSendMessage(`${TYPING_CODE}:${isTyping ? 1 : 0}`, TYPING_CODE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTyping]);

  useEffect(() => {
    const setUnReadMessagesFetch = async () => {
      try {
        if (activeUser) {
          const res =
            (await connection?.invoke<number>(
              "SetReadMessage",
              Number(activeUser?.userId ?? 0),
              Number(therapist.id)
            )) ?? -1;
          res !== -1 && setUnReadMessages(res);
          if ((activeUser.messages ?? []).find((m) => m.unRead))
            setUnReadMessagesForUser(activeUser?.userId ?? 0);
        }
      } catch (err) {
        console.error("SignalR invoke error:", err);
      }
    };
    if (connection && connection.state === "Connected") {
      setUnReadMessagesFetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeUser?.messages]);

  const messages = useMemo(() => {
    const groups: { date: string; messages: IChatMessage[] }[] = [];
    activeUser?.messages?.forEach((m) => {
      if (!m.sentDate) return;
      const date = m.sentDate.split("T")[0];
      const existing = groups.find((g) => g.date === date);
      if (existing) existing.messages.push(m);
      else groups.push({ date, messages: [m] });
    });
    return groups;
  }, [activeUser?.messages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [openSideBar, activeUser?.messages, isTyping]);

  const inputPlaceholder = !activeUser?.active
    ? t("CHAT_ENDED")
    : isConnected
    ? activeUser?.online
      ? t("CHAT_PLACEHOLDER_MESSAGE")
      : t("CHAT_USER_NOT_CONNECTED")
    : t("CHAT_NOT_CONNECTED");

  if (!mobile && !activeUser) return null;

  return (
    <>
      {mobile && openSideBar ? (
        <ChatSideBar
          setOpenSideBar={setOpenSideBar}
          openMenu={openMenu}
          activeUser={activeUser}
          setActiveUser={setActiveUser}
        />
      ) : (
        <div className={chatStyles.container} dir={i18n.dir()}>
          <div className={chatStyles.nameContainer}>
            {activeUser && (
              <>
                <Tooltip isShown={showTooltip}>
              
                  {!isConnected
                    ? t("CHAT_NOT_CONNECTED")
                    : !activeUser?.online
                    ? t("CHAT_USER_NOT_CONNECTED")
                    : ""}
                </Tooltip>
                <button
                  className={`button ${chatStyles.btn} ${chatStyles.videoBtn} ${
                    (!activeUser?.online || !isConnected) &&
                    chatStyles.sendBtn_disabled
                  }`}
                  onMouseEnter={() => setShowTooltip(true)}
                  onMouseLeave={() => setShowTooltip(false)}
                  onClick={() => {
                    if (activeUser && activeUser.online && isConnected) {
                      callUser(
                        connection,
                        {
                          name: activeUser.fullName,
                          id: activeUser.userId,
                          guid: activeUser.guid,
                        },
                        {
                          name: therapist?.fullName,
                          avatar: therapist?.avatar || null,
                        }
                      );
                    }
                  }}
                >{t("CHAT_VIDEO_CALL")}</button> 
                <img
                  src={defaultAvatar}
                  alt={activeUser?.fullName || noDataLabel}
                  className={chatStyles.avatar}
                />
                <div className={chatStyles.info}>
                  <h1
                    className={chatStyles.name}
                    data-testid="therapist-name-chat"
                  >
                    {activeUser?.fullName || noDataLabel}
                  </h1>
                  <p className={chatStyles.online} data-testid="online-status">
                    {activeUser?.online ? t("ONLINE") : t("OFFLINE")}
                  </p>
                </div>
              </>
            )}
            {mobile && (
              <button
                className={chatStyles.closeBtn}
                onClick={() => setOpenSideBar(true)}
              />
            )}
          </div>

          <div className={chatStyles.messagesWrapper}>
            <>
              {activeUser &&
                messages.map((m, index) => (
                  <div key={index} className={chatStyles.dateSection}>
                    <p className={chatStyles.date} data-testid="day-indication">
                      {getDate(m.date, t("TODAY"), t("YESTERDAY"))}
                    </p>
                    {m.messages?.map((msg, ind) => (
                      <MessageTest
                        key={ind}
                        message={msg}
                        isOwnMessage={
                          msg.senderId.toString() === therapist.id.toString()
                        }
                        activeUser={activeUser}
                      />
                    ))}
                  </div>
                ))}
              {messages.length < 1 && (
                <div className={chatStyles.text}>{t("CHAT_NO_MESSAGES")}</div>
              )}
              <div ref={messagesEndRef} />
            </>
          </div>
          {activeUser?.isTyping && (
            <ChatNote
              name={activeUser.fullName}
              type={chatNoteTypes.isTyping}
            />
          )}
          {!stream && (
            <ChatNote
              name={""}
              type={chatNoteTypes.text}
              text={t("CHAT_VIDEO_PERMISSION")}
            />
          )}
          {showCharLimitError && (
            <div
              style={{
                backgroundColor: "#fff2f0",
                border: "1px solid #ffccc7",
                borderRadius: "8px",
                padding: "8px 12px",
                margin: "0 0 8px 0",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                boxShadow: "0 2px 8px rgba(255, 77, 79, 0.1)",
                position: "relative",
              }}
            >
              <span
                style={{
                  color: "#ff4d4f",
                  fontSize: "14px",
                  fontWeight: "500",
                  flex: 1,
                  textAlign: "center",
                }}
              >
                {t("CHAT_MESSAGE_TOO_LONG")}
              </span>
              <button
                onClick={() => setShowCharLimitError(false)}
                style={{
                  background: "none",
                  border: "none",
                  color: "#ff4d4f",
                  fontSize: "16px",
                  fontWeight: "bold",
                  cursor: "pointer",
                  padding: "0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "20px",
                  height: "20px",
                  borderRadius: "50%",
                  transition: "background-color 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#ffccc7";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                }}
              >
                ×
              </button>
            </div>
          )}
          <div className={chatStyles.replyBar}>
            <textarea
              data-testid="message-textarea"
              className={`${chatStyles.message} ${chatStyles.textArea}`}
              ref={textAreaRef}
              rows={1}
              placeholder={inputPlaceholder}
              onChange={(e) => {
                const inputValue = e.target.value;
                if (inputValue.length <= 1250) {
                  setValue(inputValue);
                  startTyping();
                  setShowCharLimitError(false);
                } else {
                  // אם הטקסט ארוך מדי, נחתוך אותו ל-1250 תווים ונציג הודעת שגיאה
                  setValue(inputValue.substring(0, 1250));
                  setShowCharLimitError(true);
                }
              }}
              value={value}
              disabled={!isConnected || !activeUser || !activeUser?.active}
            />
            <button
              data-testid="send-button"
              className={`${chatStyles.btn} ${chatStyles.sendBtn} ${
                (!isConnected || !value.trim()) && chatStyles.sendBtn_disabled
              }`}
              disabled={!isConnected || !value.trim()}
              onClick={() => {
                const trimmed = value.trim();
                if (!trimmed) return;
                handleSendMessage(trimmed, "Message");
                setValue("");
              }}
            />
          </div>
        </div>
      )}
      {callEnded && (
        <Modal
          onClose={() => {
            endCall(undefined);
            acknowledgeRemoteEnd();
          }}
        >
          <CallEndedNotificationPopup
            by={callEnded}
            onClose={() => {
              endCall(undefined);
              acknowledgeRemoteEnd();
            }}
          />
        </Modal>
      )}
    </>
  );
};

export default Chat;
